import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
// import ButtonTabs from 'components/Common/ButtonTabs'
import ModalBox from 'components/Common/ModalBox'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import {
  MeetingsContainer,
  // IntegrationsContainer,
  CreateEventContainer,
  ScheduleMeetingModalBoxContainer,
  CalendarToolsContainer,
} from 'pages/Owner/container'
import {
  ConnectCalendarWrapper,
  MobileTitle,
  MobileTitleWrapper,
  ToastWrapper,
} from 'components/Calendar/style'
import GrowingContainer from 'components/Common/GrowingContainer'
import { MEETING_STEPPER, ROLES } from 'config'
import Toast from 'components/Common/Toast'
import MeetingFailedInvitesToast from './Meetings/Toasts/MeetingFailedInvitesToast'
import MeetingUpdateFailedToast from './Meetings/Toasts/MeetingUpdateFailedToast'
import CalendarDisconnectedToast from './Meetings/Toasts/CalendarDisconnectedToast'
import { getRoleFromPath } from 'utils/helper'
import { UpdateMyProfileInput, UserProfile } from '../../__generated__/api-types-and-hooks'
import { ReduxAction } from 'store/types'
import { useAuthentication } from 'utils/authentication'
import { ISelectedClient } from 'components/Calendar/MeetingSchedule'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import MeetingCancelIcon from 'components/Common/SvgIcons/MeetingCancelIcon'
import { useFlashParams } from '../../hooks/useFlashParams'
import { useQueryParams } from 'hooks/useQueryParams'

interface ICalendarProps {
  user: UserProfile
  getUserConnectorsAction
  openSlotsModalAction
  isSlotsModalOpen: boolean
  isMeetingModalOpen: boolean
  meetingStepper: MEETING_STEPPER
  updateMeetingStepperAction
  toolConnectionSuccessAction: ReduxAction<{ playId?: string; connectorName?: string }>
  getMyToolsAction: () => {}
  getOAuthUrlAction: ReduxAction<{ calendar: string; targetOrigin?: string }>
  resetOAuthUrlAction
  oAuthUrl: string
  updateUserProfileAction: ReduxAction<Partial<UpdateMyProfileInput>>
  calendarConnectionSuccess: boolean
}

const Calendar = ({
  user,
  getUserConnectorsAction,
  openSlotsModalAction,
  isSlotsModalOpen,
  isMeetingModalOpen,
  meetingStepper,
  updateMeetingStepperAction,
  toolConnectionSuccessAction,
  getMyToolsAction,
  getOAuthUrlAction,
  oAuthUrl,
  resetOAuthUrlAction,
  calendarConnectionSuccess,
}: ICalendarProps) => {
  const { t } = useTranslation()
  const [showModalBox, setShowModalBox] = useState(false)
  const [showRequestAMeetingModalBox, setShowRequestAMeetingModalBox] = useState(false)
  const [showCalendarTools, setShowCalendarTools] = useState(false)
  const [refetch, setRefetch] = useState(calendarConnectionSuccess)
  const { flashParams, removeFlashParams } = useFlashParams<{
    meetingId?: string
    failedEmails?: string
    meetingCreationStatus?: 'success' | 'failed'
    meetingEditStatus?: 'success' | 'failed'
    toastVisibilityTimeout?: string
    toastMessage?: string
    toastVariant?: 'success' | 'alert'
    connectorName: string
    calendarDisconnectedError: string
    disconnectedTools: string
  } | null>()

  useAuthentication(oAuthUrl, getUserConnectorsAction, resetOAuthUrlAction)
  const advisorMeetingRequestButtonEnabled = useFeatureFlag(
    user?.id,
    'release-advisor-schedule-meeting-rollout'
  )

  //console.log('Calender===>', user.id)
  // get clientId in order to open scheduleMeeting modal and auto select default client
  const [clientId] = useQueryParams(['clientId'])
  useEffect(() => {
    if (clientId) {
      setShowRequestAMeetingModalBox(true)
    }
  }, [clientId, setShowRequestAMeetingModalBox])

  useEffect(() => {
    if (flashParams?.connectorName) {
      toolConnectionSuccessAction({ connectorName: flashParams.connectorName })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flashParams?.connectorName])

  // const [selectedTab, setSelectedTab] = useState(tabsOptions[0])
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<null | string>(null)
  const [selectedClient, setSelectedClient] = useState<ISelectedClient | null | undefined>(null)
  const [selectedDuration, setSelectedDuration] = useState(60)
  const showInternalMeetingsOnly = false

  const onCloseHandler = () => {
    setRefetch(!refetch)
    openSlotsModalAction()
    setShowModalBox(false)
    setSelectedAdvisorId(null)
    setShowCalendarTools(false)
    setShowRequestAMeetingModalBox(false)
    setSelectedDuration(60)
    updateMeetingStepperAction({ actionType: 'reset', stepName: MEETING_STEPPER.ADVISOR })
  }
  const role = getRoleFromPath()
  useEffect(() => {
    if (isSlotsModalOpen || (isMeetingModalOpen && !role?.includes(ROLES.BUSINESS_OWNER))) {
      onCloseHandler()
    }
    // eslint-disable-next-line
  }, [isSlotsModalOpen, isMeetingModalOpen])

  useEffect(() => {
    getUserConnectorsAction()
  }, [getUserConnectorsAction, getMyToolsAction])

  const handleClose = () => {
    setShowModalBox(false)
    setSelectedAdvisorId(null)
    setSelectedClient(null)
    setShowRequestAMeetingModalBox(false)
    updateMeetingStepperAction({ actionType: 'reset', stepName: MEETING_STEPPER.ADVISOR })
  }

  const handleCloseMeetingToast = useCallback(() => {
    removeFlashParams()
  }, [removeFlashParams])

  useEffect(() => {
    if (flashParams?.toastVisibilityTimeout) {
      const timeout = setTimeout(() => {
        handleCloseMeetingToast()
      }, Number(flashParams.toastVisibilityTimeout))

      return () => clearTimeout(timeout)
    }
    return () => {}
  }, [flashParams?.toastVisibilityTimeout, handleCloseMeetingToast])

  const isUserBusinessOwner = (): boolean => {
    const roles = user?.roles ?? []
    return roles[0] === 'BUSINESS_OWNER'
  }

  const isUserBSOUser = (): boolean => {
    const roles = user?.roles ?? []
    return roles.includes('BSO_ADVISOR') || roles.includes('BSO_ADMIN')
  }

  useEffect(() => {
    if (calendarConnectionSuccess) {
      setRefetch(true)
    }
  }, [calendarConnectionSuccess, setRefetch])

  const disconnectedTools = flashParams?.disconnectedTools?.split(', ')

  return (
    <GrowingContainer>
      <ConnectCalendarWrapper>
        <MobileTitleWrapper>
          <MobileTitle>{t('calendar.title')}</MobileTitle>
        </MobileTitleWrapper>
        {flashParams?.failedEmails?.split(',')?.length && (
          <ToastWrapper>
            <MeetingFailedInvitesToast
              onClose={handleCloseMeetingToast}
              failedEmails={flashParams?.failedEmails ?? ''}
              lastCreatedMeetingId={flashParams.meetingId ?? ''}
            />
          </ToastWrapper>
        )}
        {(flashParams?.meetingCreationStatus || flashParams?.meetingEditStatus === 'success') &&
          !flashParams?.failedEmails?.split(',')?.length && (
            <ToastWrapper>
              <Toast
                variant={flashParams?.toastVariant}
                showCloseButton={true}
                onClose={handleCloseMeetingToast}
              >
                {flashParams?.toastMessage}
              </Toast>
            </ToastWrapper>
          )}
        {flashParams?.meetingEditStatus === 'failed' && (
          <ToastWrapper>
            <MeetingUpdateFailedToast
              onClose={handleCloseMeetingToast}
              meetingId={flashParams.meetingId ?? ''}
            />
          </ToastWrapper>
        )}
        {flashParams?.disconnectedTools &&
          disconnectedTools?.map((tool) => (
            <ToastWrapper>
              <CalendarDisconnectedToast
                onClose={handleCloseMeetingToast}
                onClick={() => {
                  getOAuthUrlAction({ calendar: tool, targetOrigin: window.location.href })
                }}
                tool={tool}
              />
            </ToastWrapper>
          ))}

        {/* <div className="col-span-12 items-center justify-between">
          <ButtonTabs
            options={tabsOptions}
            setSelectedTab={onHandleTabChange}
            selectedTab={selectedTab}
            size="xl"
            fontSize="sm:!text-2xl"
          />
        </div> */}
        <div
          id="calendar-event-panel"
          className="col-span-12 relative z-[5] xs:mt-2 sm:mt-4 md:mt-3 lg:mt-5"
        >
          <div
            id="calendar-panel"
            className="xs:block md:grid bg-white grid-cols-12  xs:pt-[20px] xs:px-[15px] sm:pt-[25px] sm:px-[37px] lg:p-[34px]  rounded-2xl mb-8 gap-12"
          >
            <MeetingsContainer
              refetch={refetch}
              showInternalMeetingsOnly={showInternalMeetingsOnly}
              advisorMeetingRequestButtonEnabled={advisorMeetingRequestButtonEnabled}
            />
            {/* {selectedTab.label === 'EVENTS' && (
              <MeetingsContainer refetch={refetch} setShowModalBox={setShowModalBox} />
            )} */}
            {/* {selectedTab.label === 'CALENDARS' && <IntegrationsContainer tenantId={tenantId} />} */}
          </div>
        </div>
        {/* Meeting scheduling */}
        {showModalBox && isUserBusinessOwner() ? (
          <ModalBox
            height={'xs:h-full sm:h-auto max-h-full'}
            margin="sm:mx-5"
            onClose={handleClose}
            title={meetingStepper === MEETING_STEPPER.MEETING_CONFIRMATION ? '' : meetingStepper}
            onBack={
              meetingStepper === MEETING_STEPPER.ADVISOR ||
              meetingStepper === MEETING_STEPPER.MEETING_CONFIRMATION
                ? undefined
                : () => updateMeetingStepperAction({ actionType: 'decrement' })
            }
            Icon={MeetingCancelIcon}
          >
            <ScheduleMeetingModalBoxContainer
              setSelectedAdvisorId={setSelectedAdvisorId}
              selectedAdvisorId={selectedAdvisorId}
              setShowModalBox={setShowModalBox}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              isBSOUser={isUserBSOUser()}
              setSelectedDuration={setSelectedDuration}
              selectedDuration={selectedDuration}
            />
          </ModalBox>
        ) : (
          showModalBox &&
          isUserBSOUser() && (
            <div>
              <ModalBox
                title={t('calendar.meetingSettings')}
                onClose={onCloseHandler}
                height="h-full"
                padding="xs:p-6 sm:p-7"
                margin="sm:py-4"
              >
                <CreateEventContainer onClose={setShowModalBox} />
              </ModalBox>
            </div>
          )
        )}
        {showCalendarTools && (
          <div>
            <ModalBox
              title="Connect with Calendar"
              onClose={onCloseHandler}
              height="xs:h-screen sm:h-auto"
              position="desktopTop"
            >
              <CalendarToolsContainer />
            </ModalBox>
          </div>
        )}
        {showRequestAMeetingModalBox && (
          <div>
            <ModalBox
              title={MEETING_STEPPER.ADVISOR}
              onClose={handleClose}
              height="xs:h-screen sm:h-auto"
              position="top"
              onBack={
                meetingStepper === MEETING_STEPPER.ADVISOR
                  ? undefined
                  : () => updateMeetingStepperAction({ actionType: 'decrement' })
              }
            >
              <ScheduleMeetingModalBoxContainer
                setSelectedAdvisorId={setSelectedAdvisorId}
                selectedAdvisorId={selectedAdvisorId}
                setShowModalBox={setShowRequestAMeetingModalBox}
                selectedClient={selectedClient}
                setSelectedClient={setSelectedClient}
                selectedDuration={selectedDuration}
                setSelectedDuration={setSelectedDuration}
                isBSOUser={isUserBSOUser()}
              />
            </ModalBox>
          </div>
        )}
      </ConnectCalendarWrapper>
    </GrowingContainer>
  )
}
export default Calendar
